
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { showSuccessToast } from '@/utils/toast-utils';
import {
  ChevronLeft,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { format, parse, set, isEqual, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns';
import CalendarWrapper from '../shared/CalendarWrapper';
import { getWeeklyAvailabilitySchedule, WeeklyAvailabilitySchedule } from '@/lib/api/availability';

type TimeSlot = {
  id: string;
  time: string;
  available: boolean;
};

export const AvailabilityCalendar = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
  const [schedule, setSchedule] = useState<WeeklyAvailabilitySchedule | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [timezone, setTimezone] = useState(() => {
    // Try to guess user's timezone or default to ET
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
    } catch (e) {
      return 'America/New_York';
    }
  });

  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [unavailableDates, setUnavailableDates] = useState<Date[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);

  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        setIsLoading(true);
        const data = await getWeeklyAvailabilitySchedule();
        setSchedule(data);
      } catch (error) {
        console.error('Failed to fetch availability schedule:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchSchedule();
  }, []);

  useEffect(() => {
    if (!schedule) return;

    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    const daysInMonth = eachDayOfInterval({ start, end });

    const available: Date[] = [];
    const unavailable: Date[] = [];
    const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    daysInMonth.forEach(date => {
      const dayStr = daysMap[getDay(date)];
      const daySchedule = schedule.days.find(d => d.day === dayStr);

      if (daySchedule && daySchedule.isEnabled && daySchedule.slots.length > 0) {
        available.push(date);
      } else {
        unavailable.push(date);
      }
    });

    setAvailableDates(available);
    setUnavailableDates(unavailable);
  }, [schedule, calendarMonth]);

  useEffect(() => {
    if (!schedule) return;
    const daysMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const selectedDayStr = daysMap[getDay(selectedDate)];
    const selectedDaySchedule = schedule.days.find(d => d.day === selectedDayStr);

    if (selectedDaySchedule && selectedDaySchedule.isEnabled) {
      setTimeSlots(
        selectedDaySchedule.slots.map((slot, index) => ({
          id: `${selectedDayStr}-${index}`,
          time: `${slot.startTime} - ${slot.endTime}`,
          available: true,
        }))
      );
    } else {
      setTimeSlots([]);
    }
  }, [schedule, selectedDate]);

  // Handler for saving availability
  const handleSave = () => {
    showSuccessToast("Availability saved", "Your availability settings have been saved successfully");
  };

  // Handler for selecting a time slot
  const toggleTimeSlot = (slotId: string) => {
    setTimeSlots(prev =>
      prev.map(slot =>
        slot.id === slotId ? { ...slot, available: !slot.available } : slot
      )
    );
  };

  // Handler for navigating months
  const handleMonthChange = (newMonth: Date) => {
    setCalendarMonth(newMonth);
  };

  const handlePrevMonth = () => {
    setCalendarMonth(prevMonth => subMonths(prevMonth, 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(prevMonth => addMonths(prevMonth, 1));
  };

  return (
    <div className="space-y-6">
      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-cs-primary" />
        </div>
      ) : (
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Left side: Calendar */}
        <div className="w-full lg:w-7/12">
          <Card className="overflow-hidden">
            <CardContent className="p-0">

              <div className="bg-white p-4">
                {/* Month navigation */}
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold">{format(calendarMonth, 'MMMM yyyy')}</h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={handlePrevMonth}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={handleNextMonth}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Calendar */}
                <div className="calendar-grid">
                  {/* Days of week headers */}
                  <div className="grid grid-cols-7 text-center mb-2">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                      <div key={day} className="text-sm font-medium text-gray-500">
                        {day}
                      </div>
                    ))}
                  </div>

                  <CalendarWrapper
                    selectedDate={selectedDate}
                    onDateSelect={(date) => date && setSelectedDate(date)}
                    calendarMonth={calendarMonth}
                    onMonthChange={handleMonthChange}
                    className="border-none shadow-none"
                    showHeader={false}
                    modifiers={{
                      available: availableDates,
                      unavailable: unavailableDates,
                    }}
                    modifiersClassNames={{
                      selected: 'bg-red-500 text-white hover:bg-red-500 hover:text-white rounded-full',
                      today: 'border-2 border-cs-primary font-medium',
                      available: 'text-green-700 bg-green-50 rounded-full font-medium',
                      unavailable: 'text-gray-300 cursor-not-allowed',
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right side: Selected date and time slots */}
        <div className="w-full lg:w-5/12">
          <Card>
            <CardContent className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold mb-1">{format(selectedDate, 'EEEE, MMM d')}</h2>
                <p className="text-gray-500 text-sm mb-6">{format(selectedDate, 'yyyy')}</p>

                <div className="space-y-3">
                  <h3 className="text-lg font-medium">Available Times</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {timeSlots.map(slot => (
                      <Button
                        key={slot.id}
                        variant={slot.available ? "default" : "outline"}
                        className={`w-full ${slot.available ? 'bg-cs-primary text-white' : 'text-cs-primary border-cs-primary'}`}
                        onClick={() => toggleTimeSlot(slot.id)}
                      >
                        {slot.time}
                      </Button>
                    ))}
                    <Button variant="ghost" className="border border-dashed border-gray-300">
                      + Add Time
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 flex justify-end">
            <Button
              onClick={handleSave}
              className="bg-cs-primary hover:bg-cs-primary/90"
            >
              Save Availability
            </Button>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};
